import * as React from 'react';
import type { DriverSortBy, GetDriversResponseDTO } from '@relay-towing/api';
import {
  Button,
  DriverCard,
  MobileHeader,
  SearchField,
  Stack,
} from '@relay-towing/ui';
import { Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useMember } from '../../hooks/useMember';
import { usePagedList } from '../../hooks/usePagedList';
import { PagedRows } from '../shell/PagedRows';
import { useBack } from '../shell/useBack';
import { TabScreen } from '../shell/TabScreen';
import { dutyLabel } from '../status';
import { AddDriverSheet } from './AddDriverSheet';

export type DriverRow = GetDriversResponseDTO;

/** How often the list asks again while in view, so duty statuses move. */
const POLL_DRIVERS_MS = 30_000;

/**
 * Drivers, from the Operations tab, for an office role with "View all
 * calls". No Figma frame exists for it: the layout is inferred, and a
 * designer's frame replaces it. Everyone who drives, with their duty status
 * and truck, searched on the server; tap one for their day. With "Add
 * drivers", Add opens the form to add one.
 *
 * It had the trucks on a map above the list, which the owner took off
 * (2026-10-28): where everyone is, is the Calls tab's map.
 */
export function DriversScreen() {
  const navigate = useNavigate();
  const back = useBack('/operations');
  const member = useMember();
  const [adding, setAdding] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const searchQuery = useDebouncedValue(search).trim() || undefined;
  const list = usePagedList<DriverRow>(
    'mobile-drivers',
    '/drivers',
    { sortBy: 'name' satisfies DriverSortBy, searchQuery },
    { pollEvery: POLL_DRIVERS_MS }
  );

  return (
    <TabScreen
      header={
        <MobileHeader
          onBack={back}
          backLabel="Back to operations"
          title="Drivers"
          action={
            member.can('drivers.add') ? (
              <Button
                variant="secondary"
                size="icon-lg"
                pill
                aria-label="Add driver"
                onClick={() => setAdding(true)}
              >
                <Plus />
              </Button>
            ) : undefined
          }
        />
      }
    >
      <AddDriverSheet open={adding} onOpenChange={setAdding} />
      <Stack gap={6}>
        <SearchField
          value={search}
          onChange={setSearch}
          label="Search drivers"
          placeholder="Name, phone or truck"
        />
        <PagedRows list={list} empty="No drivers yet" toolbar={null}>
          {(driver) => (
            <DriverCard
              key={driver.id}
              platform="phone"
              onClick={() => navigate(`/drivers/${driver.id}`)}
              employeeNumber={driver.employeeNumber}
              name={driver.name}
              truckNumber={driver.truckNumber}
              status={driver.status}
              statusLabel={dutyLabel(driver.status)}
              phone={driver.phone}
            />
          )}
        </PagedRows>
      </Stack>
    </TabScreen>
  );
}
